/**
 * Giving one line back, not the whole bill.
 *
 * A customer paid for five things and is unhappy with one. The whole-bill
 * refund is the wrong tool — it would hand back the four they were happy with
 * too. This picks the single line, and the amount is not typed: a line is worth
 * exactly its share of what was paid, so the server works it out and this only
 * has to say which line and why.
 *
 * WHY A REASON, STILL
 * -------------------
 * The same reason a whole refund needs one — money leaving with nothing sold is
 * the easiest thing to not account for later. The reason is recorded against
 * the person at critical severity, and it names the dish, so the day's refunds
 * read "Zinger Burger — Food quality", not a bare amount.
 */
import { useEffect, useState } from 'react';
import { formatPKR } from '@foodrun/shared';

import { Button } from '../../components/ui/Button.jsx';

/** The reasons that actually come up, the same as a whole-bill refund. */
const REASONS = ['Wrong item', 'Food quality', 'Missing', 'Customer changed mind', 'Billing error'];

/**
 * What a line will actually give back — its share of what was paid, not its
 * sticker price. Where a bill had a discount, a line is worth less than it
 * looks; where it had tax, more. This mirrors the server's split exactly (see
 * the item-refund endpoint) so the amount shown is the amount that moves.
 */
function refundAmountOf(item, totals) {
  const subtotal = Number(totals?.subtotal) || 0;
  const gross = Number(item?.total) || 0;
  const fraction = subtotal > 0 ? gross / subtotal : 1;
  const net = subtotal - (Number(totals?.totalDiscount) || 0);
  const reverseNet = Math.round(net * fraction * 100) / 100;
  return Math.round((reverseNet + (Number(totals?.totalTax) || 0) * fraction) * 100) / 100;
}

export function ItemRefundPanel({ items, totals, invoiceNumber, onRefund, onCancel, busy, error }) {
  // Which line is being refunded, and why. Null until a line is picked.
  const [pickedId, setPickedId] = useState(null);
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');

  const picked = (items ?? []).find((i) => i.id === pickedId) || null;
  const valid = picked && !picked.refunded && reason;

  // Once the picked line comes back refunded, collapse the form — the bill has
  // refetched, the line is struck through, and there is nothing more to give
  // back on it. Any other line can still be picked.
  useEffect(() => {
    if (picked?.refunded) {
      setPickedId(null);
      setReason('');
      setDetail('');
    }
  }, [picked?.refunded]);

  return (
    <div>
      <h2 className="font-semibold text-lg text-ink">Refund an item — {invoiceNumber}</h2>
      <p className="mt-0.5 text-sm text-ink-2">
        Pick the line to give back. It stays on the bill, struck through, and its amount comes off.
        Recorded against your name.
      </p>

      <ul className="mt-4 divide-y divide-rule rounded-lg border border-rule">
        {(items ?? []).map((item) => {
          const isPicked = item.id === pickedId;
          return (
            <li key={item.id} className="px-3 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className={item.refunded ? 'text-ink-3 line-through' : 'text-ink'}>
                  <span className="text-[15px]">{item.name}</span>
                  {item.quantity > 1 && <span className="ml-1 text-sm text-ink-2">×{item.quantity}</span>}
                  <span className="ml-2 tabular-nums text-sm text-ink-2">
                    {formatPKR(refundAmountOf(item, totals))}
                  </span>
                </div>
                {item.refunded ? (
                  <span className="shrink-0 rounded-md bg-surface-3 px-2 py-1 text-xs text-ink-2">
                    Refunded
                  </span>
                ) : (
                  <Button
                    variant={isPicked ? 'primary' : 'secondary'}
                    className="shrink-0"
                    onClick={() => {
                      setPickedId(item.id);
                      setReason('');
                      setDetail('');
                    }}
                    disabled={busy}
                  >
                    {isPicked ? 'Chosen' : 'Refund'}
                  </Button>
                )}
              </div>

              {/* The reason opens under the line that was picked, so it is
                  unmistakable which item the reason is being given for. */}
              {isPicked && !item.refunded && (
                <div className="mt-3">
                  <fieldset>
                    <legend className="mb-1.5 text-sm font-medium text-ink-2">Why?</legend>
                    <div className="flex flex-wrap gap-1.5">
                      {REASONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          aria-pressed={reason === option}
                          onClick={() => setReason(option)}
                          className={`h-10 rounded-lg px-3 text-sm font-medium transition-colors ${
                            reason === option
                              ? 'bg-brand-400 text-surface-2'
                              : 'bg-surface-3 text-ink-2 hover:bg-rule-strong'
                          }`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </fieldset>

                  <label className="mt-3 block">
                    <span className="mb-1.5 block text-sm font-medium text-ink-2">
                      Anything else worth recording (optional)
                    </span>
                    <input
                      type="text"
                      value={detail}
                      onChange={(e) => setDetail(e.target.value)}
                      className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-ink text-[15px] focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
                    />
                  </label>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {error.message}
        </p>
      )}

      <div className="mt-5 flex gap-2">
        <Button variant="secondary" size="lg" onClick={onCancel} disabled={busy}>
          Back
        </Button>
        <Button
          variant="danger"
          size="lg"
          className="flex-1"
          busy={busy}
          disabled={!valid}
          onClick={() =>
            onRefund(picked.id, {
              reason: detail.trim() ? `${reason} — ${detail.trim()}` : reason,
              refundMethod: 'cash',
            })
          }
        >
          {picked ? `Refund ${formatPKR(refundAmountOf(picked, totals))}` : 'Pick an item'}
        </Button>
      </div>
    </div>
  );
}
